import { buildQueryParams, cleanParams } from "@/utils";

/**
 * Создает новую заявку.
 * @param {Object} item - Данные заявки. Могут быть как JSON, так и FormData.
 * @returns {Promise<Object>} - Созданная заявка.
 * @throws {Error} - Ошибка при создании данных.
 */
export const createRequest = async (item) => {
  try {
    const isForm = item instanceof FormData;
    const response = await fetch(`/api/public/request-entry`, {
      method: "POST",
      headers: isForm ? undefined : { "Content-Type": "application/json" },
      body: isForm ? item : JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при отправке: ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.task;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение списка типов заявок.
 * @param {boolean} isAdmin - указывает, используется ли доступ администратора
 * @returns {Promise} - список типов
 * @throws {Error} - ошибка при получении данных
 */
export const getList = async (isAdmin = false) => {
  const endpoint = isAdmin ? "/api/requesttypes/admin" : "/api/requesttypes";

  try {
    const response = await fetch(endpoint, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data.requestTypes;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение одного типа заявки по UUID.
 * @param {boolean} isAdmin - флаг доступа администратора
 * @param {Object} params - { uuid?: string }
 * @returns {Promise} - найденный тип заявки или null
 * @throws {Error} - ошибка при получении данных
 */
export const getItem = async (isAdmin = false, params = {}) => {
  if (!params.uuid) {
    throw new Error("Ошибка при получении данных: не передан uuid");
  }

  const baseUrl = isAdmin ? "/api/requesttypes/admin" : "/api/requesttypes";

  const query = `uuid=${encodeURIComponent(params.uuid)}`;

  const endpoint = `${baseUrl}?${query}`;

  try {
    const response = await fetch(endpoint, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data.requestTypes[0];
  } catch (err) {
    throw err;
  }
};

/**
 * Получение данных заявки по uuid для публичной страницы
 * @param {Object} params - { uuid?: string }
 * @returns {Promise} - найденная заявка или null
 * @throws {Error} - ошибка при получении данных
 */
export const getPublicItem = async (uuid) => {
  try {
    const response = await fetch(`/api/public/requesttype?uuid=${uuid}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data.requestType;
  } catch (err) {
    throw err;
  }
};

/**
 * Обновление типа заявки
 * @param {Object} item - данные типа заявки
 * @returns {Promise} - обновленный тип заявки
 * @throws {Error} - ошибка при обновлении данных
 */
export const updateItem = async (item) => {
  try {
    const response = await fetch("/api/requesttype", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении данных: ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.requestType;
  } catch (err) {
    throw err;
  }
};

/**
 * Привязать список рабочих групп к типу заявки
 * @param {Object} item - данные заявки и список рабочих групп
 * @returns {Promise<Object>} - обновленная заявка
 * @throws {Error} - ошибка при обновлении данных
 */
export const setUnits = async (item) => {
  try {
    const response = await fetch("/api/requesttypes/units", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при добавлении списка: ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.units;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение списка моих заявок
 * @returns {Promise<Array>} - список моих заявок
 * @throws {Error} - ошибка при получении данных
 */
export const getMyRequests = async (params) => {
  const cleanedFilters = cleanParams(params);
  const queryString = buildQueryParams(cleanedFilters);

  try {
    const response = await fetch(`/api/request-entries/my?${queryString}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении моих заявок. ${data.error || "Обновите страницу"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

export const getRequestEntry = async ({ uuid }) => {
  try {
    const response = await fetch(
      `/api/public/request-entry/${encodeURIComponent(uuid)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      const error = new Error(
        `Ошибка при получении заявки. ${data.error || "Обновите страницу"}`,
      );

      error.status = response.status;
      error.original = data.error;

      throw error;
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};
export const cancelMyRequest = async ({ uuid, reason }) => {
  try {
    const response = await fetch(
      `/api/public/request-entry/${encodeURIComponent(uuid)}/cancel`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ reason }),
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при отмене заявки. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение списка форм типов заявок.
 * @returns {Promise} - список типов
 * @throws {Error} - ошибка при получении данных
 */
export const getFormList = async (isAdmins = false) => {
  try {
    const response = await fetch(
      `/api/request-forms${isAdmins ? "/admin" : ""}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение одной формы типа заявки по UUID.
 * @param {Object} params - { uuid?: string }
 * @returns {Promise} - найденный тип заявки или null
 * @throws {Error} - ошибка при получении данных
 */
export const getFormItem = async (params = {}, isAdmins = false) => {
  if (!params.uuid) {
    throw new Error("Ошибка при получении данных: не передан uuid");
  }

  const baseUrl = `/api${isAdmins ? "" : "/public"}/request-form${isAdmins ? "/admin" : ""}`;

  const query = `uuid=${encodeURIComponent(params.uuid)}`;

  const endpoint = `${baseUrl}?${query}`;

  try {
    const response = await fetch(endpoint, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      const error = new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );

      error.status = response.status;
      error.original = data.error;

      throw error;
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Создает новую форму для типа заявки.
 * @param {Object} item - Данные формы.
 * @returns {Promise<Object>} - Созданная форма.
 * @throws {Error} - Ошибка при создании данных.
 */
export const createForm = async (item) => {
  try {
    const response = await fetch(`/api/request-form`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при отправке: ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Обновление формы типа заявки
 * @param {Object} item - данные формы
 * @returns {Promise} - обновленная форма
 * @throws {Error} - ошибка при обновлении данных
 */
export const updateForm = async (item) => {
  try {
    const response = await fetch("/api/request-form", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении данных: ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление формы типа заявки
 * @param {Object} item - данные формы
 * @returns {Promise} - удаленная форма
 * @throws {Error} - ошибка при удалении данных
 */
export const deleteForm = async (item) => {
  try {
    const response = await fetch("/api/request-form", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Создает новую группу полей для типа заявки.
 * @param {Object} item - Данные группы.
 * @returns {Promise<Object>} - Созданная группа.
 * @throws {Error} - Ошибка при создании данных.
 */
export const createGroup = async (item) => {
  try {
    const response = await fetch(`/api/request-form/group`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при отправке: ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Обновление группы полей для типа заявки
 * @param {Object} item - данные группы
 * @returns {Promise} - обновленная группа
 * @throws {Error} - ошибка при обновлении данных
 */
export const updateGroup = async (item) => {
  try {
    const response = await fetch("/api/request-form/group", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении данных: ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление группы полей для типа заявки
 * @param {Object} item - данные
 * @returns {Promise} - удаленная группа
 * @throws {Error} - ошибка при удалении данных
 */
export const deleteGroup = async (item) => {
  try {
    const response = await fetch("/api/request-form/group", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Создает новое поле для группы полей для типа заявки.
 * @param {Object} item - Данные поля.
 * @returns {Promise<Object>} - Созданное поле.
 * @throws {Error} - Ошибка при создании данных.
 */
export const createField = async (item) => {
  try {
    const response = await fetch(`/api/request-form/field`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при отправке: ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Обновление поля для группы полей для типа заявки.
 * @param {Object} item - данные поля
 * @returns {Promise} - обновленное поле
 * @throws {Error} - ошибка при обновлении данных
 */
export const updateField = async (item) => {
  try {
    const response = await fetch("/api/request-form/field", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении данных: ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление поля для группы полей для типа заявки.
 * @param {Object} item - данные поля
 * @returns {Promise} - удаленное поле
 * @throws {Error} - ошибка при удалении данных
 */
export const deleteField = async (item) => {
  try {
    const response = await fetch("/api/request-form/field", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.group;
  } catch (err) {
    throw err;
  }
};

/**
 * Создает опцию поля типа select для группы полей для типа заявки.
 * @param {Object} item - Данные опции.
 * @returns {Promise<Object>} - Созданная опция.
 * @throws {Error} - Ошибка при создании данных.
 */
export const createSelectOption = async (item) => {
  try {
    const response = await fetch(`/api/request-form/field/option`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при отправке: ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Обновление опции поля типа select для группы полей для типа заявки.
 * @param {Object} item - данные опции
 * @returns {Promise} - обновленная опция
 * @throws {Error} - ошибка при обновлении данных
 */
export const updateSelectOption = async (item) => {
  try {
    const response = await fetch("/api/request-form/field/option", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении данных: ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление опции поля типа select для группы полей для типа заявки.
 * @param {Object} item - данные опции
 * @returns {Promise} - удаленная опция
 * @throws {Error} - ошибка при удалении данных
 */
export const deleteSelectOption = async (item) => {
  try {
    const response = await fetch("/api/request-form/field/option", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Получение списка рабочих групп для типа заявки.
 * @param {Object} params - { request_form_uuid?: string }
 * @returns {Promise} - список РГ
 * @throws {Error} - ошибка при получении данных
 */
export const getRequestUnitsList = async (params = {}) => {
  const baseUrl = "/api/request-form/units";

  const query = params.request_form_uuid
    ? `request_form_uuid=${encodeURIComponent(params.request_form_uuid)}`
    : "";

  const endpoint = `${baseUrl}?${query}`;

  try {
    const response = await fetch(endpoint, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при получении данных. ${data.error || "Обновите страницу"}`,
      );
    }

    return data.units;
  } catch (err) {
    throw err;
  }
};

/**
 * Привязать рабочую групп к типу заявки
 * @param {Object} item - данные заявки и рабочей группы: unit_displayed_name - название подразделения, comment - комментарий, request_form_uuid - uuid типа заявки, unit_uuid - uuid рабочей группы
 * @returns {Promise<Object>} - обновленная заявка
 * @throws {Error} - ошибка при обновлении данных
 */
export const setUnit = async (item) => {
  try {
    const response = await fetch("/api/request-form/unit", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при добавлении списка: ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Редактировать привязку рабочей группы к типу заявки
 * @param {Object} item - данные заявки и рабочей группы: unit_displayed_name - название подразделения, comment - комментарий, request_form_uuid - uuid типа заявки, unit_uuid - uuid рабочей группы
 * @returns {Promise<Object>} - обновленная заявка
 * @throws {Error} - ошибка при обновлении данных
 */
export const updateUnit = async (item) => {
  try {
    const response = await fetch("/api/request-form/unit", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при добавлении списка: ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление связи с рабочей группы типа заявки
 * @param {Object} item - данные рабочей группы
 * @returns {Promise} - удаленная рг
 * @throws {Error} - ошибка при удалении данных
 */
export const deleteUnit = async (item) => {
  try {
    const response = await fetch("/api/request-form/unit", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Обновление шаблона заголовка задачи типа заявки
 * @param {Object} params - {uuid, item} - Идентификатор формы, Данные шаблона.
 * @returns {Promise} - обновленная шаблона
 * @throws {Error} - ошибка при обновлении данных
 */
export const updateTaskTitleTemplate = async ({uuid, task_name_template}) => {
  try {
    const response = await fetch(`/api/request-forms/${uuid}/task-name-template`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({task_name_template: task_name_template}),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при обновлении данных: ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Добавляет целевую РГ для формы
 * @param {Object} item - Данные РГ и формы.
 * @returns {Promise<Object>} - Созданная связь.
 * @throws {Error} - Ошибка при создании данных.
 */
export const createAccessUnit = async (item) => {
  try {
    const response = await fetch(`/api/request-form/access-unit`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при отправке: ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};

/**
 * Удаление целевой РГ для формы
 * @param {Object} item - данные формы и рг
 * @returns {Promise} - удаленная связь
 * @throws {Error} - ошибка при удалении данных
 */
export const deleteAccessUnit = async (item) => {
  try {
    const response = await fetch("/api/request-form/access-unit", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(item),
    });

    const data = await response.json();

    if (!response.ok || data.error) {
      throw new Error(
        `Ошибка при удалении данных. ${data.error || "Попробуйте снова"}`,
      );
    }

    return data.data;
  } catch (err) {
    throw err;
  }
};
